import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

const inboundPermitResponseSchema = z.object({
  message: z.string(),
});

// ---------------------------------------------------------------------------
// Permit grant status — CWL-411 (absorbs CWL-144)
// ---------------------------------------------------------------------------

/**
 * Permit outcome relayed by CORE on a `status` permit_update: Cadent approval,
 * SAP request, authority grant/refusal, and terminal "no permit" states.
 * DISTINCT from PERMIT_STATUS (the Works Lite permit lifecycle). Pre-SAP values
 * are stored on works_lite_permit_request because no permit row exists yet.
 *
 * Final values depend on CWL-410. Adding one is data only: a member here, a
 * label below (Record forces it) and WORKS_LITE_PERMIT_GRANT_STATUS in the
 * backend schema (a pin test forces it). The handler has no per-value branches.
 */
export const PERMIT_GRANT_STATUS = {
  PENDING_APPROVAL: 'pending_approval',
  APPROVAL_DECLINED: 'approval_declined',
  REQUESTED: 'requested',
  GRANTED: 'granted',
  REFUSED: 'refused',
  NOT_GENERATED: 'not_generated',
  INVALID: 'invalid',
  WITHDRAWN: 'withdrawn',
  REVOKE_PROPOSED: 'revoke_proposed',
  REVOKE_IN_PROGRESS: 'revoke_in_progress',
  /** CORE sent a value outside the set above. Stored, never rejected (CWL-144). */
  UNRECOGNISED: 'unrecognised',
} as const;

export const permitGrantStatusSchema = z.enum([
  PERMIT_GRANT_STATUS.PENDING_APPROVAL,
  PERMIT_GRANT_STATUS.APPROVAL_DECLINED,
  PERMIT_GRANT_STATUS.REQUESTED,
  PERMIT_GRANT_STATUS.GRANTED,
  PERMIT_GRANT_STATUS.REFUSED,
  PERMIT_GRANT_STATUS.NOT_GENERATED,
  PERMIT_GRANT_STATUS.INVALID,
  PERMIT_GRANT_STATUS.WITHDRAWN,
  PERMIT_GRANT_STATUS.REVOKE_PROPOSED,
  PERMIT_GRANT_STATUS.REVOKE_IN_PROGRESS,
  PERMIT_GRANT_STATUS.UNRECOGNISED,
]);
export type PermitGrantStatus = z.infer<typeof permitGrantStatusSchema>;

/** Display labels. Record<PermitGrantStatus, string>: a new member without a label is a compile error. */
export const PERMIT_GRANT_STATUS_LABEL: Record<PermitGrantStatus, string> = {
  [PERMIT_GRANT_STATUS.PENDING_APPROVAL]: 'Awaiting Cadent approval',
  [PERMIT_GRANT_STATUS.APPROVAL_DECLINED]: 'Declined by Cadent',
  [PERMIT_GRANT_STATUS.REQUESTED]: 'Requested',
  [PERMIT_GRANT_STATUS.GRANTED]: 'Granted',
  [PERMIT_GRANT_STATUS.REFUSED]: 'Refused',
  [PERMIT_GRANT_STATUS.NOT_GENERATED]: 'No permit generated',
  [PERMIT_GRANT_STATUS.INVALID]: 'Invalid',
  [PERMIT_GRANT_STATUS.WITHDRAWN]: 'Withdrawn',
  [PERMIT_GRANT_STATUS.REVOKE_PROPOSED]: 'Revocation proposed',
  [PERMIT_GRANT_STATUS.REVOKE_IN_PROGRESS]: 'Revocation in progress',
  [PERMIT_GRANT_STATUS.UNRECOGNISED]: 'Not recognised',
};

/**
 * SAP permit task code → grant status (CWL-410 table, 2026-09-30). Correct the mapping here only.
 * No grant code is listed: a permit is granted only by CORE's `granted` status, which it sends once
 * SAP supplies the permit reference number (SAP `PermitRefNo`, CWL-144).
 */
export const PERMIT_GRANT_STATUS_BY_SAP_CODE = {
  PREF: PERMIT_GRANT_STATUS.REFUSED,
  RPPW: PERMIT_GRANT_STATUS.REVOKE_PROPOSED,
  RPIP: PERMIT_GRANT_STATUS.REVOKE_IN_PROGRESS,
} as const satisfies Record<string, PermitGrantStatus>;

function toWireToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');
}

/**
 * Former SAP grant task codes. They no longer grant (CWL-144): CORE sends `granted` itself once SAP
 * supplies the permit reference number, and never forwards these codes. One that still arrives is
 * ignored rather than stored as unrecognised, so it cannot overwrite a real grant status.
 */
export const RETIRED_PERMIT_GRANT_CODES = ['GPER', 'GPAA', 'GPVR'] as const;
const RETIRED_PERMIT_GRANT_TOKENS: ReadonlySet<string> = new Set(
  RETIRED_PERMIT_GRANT_CODES.map(toWireToken),
);

export function isRetiredPermitGrantCode(value: unknown): boolean {
  return typeof value === 'string' && RETIRED_PERMIT_GRANT_TOKENS.has(toWireToken(value));
}

// Every recognised code and its display label, in wire-token form. Derived, so a
// new member is picked up with no code change. UNRECOGNISED is never matched.
const PERMIT_GRANT_STATUS_BY_WIRE_TOKEN: ReadonlyMap<string, PermitGrantStatus> = new Map([
  ...permitGrantStatusSchema.options
    .filter((status) => status !== PERMIT_GRANT_STATUS.UNRECOGNISED)
    .flatMap((status): [string, PermitGrantStatus][] => [
      [toWireToken(status), status],
      [toWireToken(PERMIT_GRANT_STATUS_LABEL[status]), status],
    ]),
  ...Object.entries(PERMIT_GRANT_STATUS_BY_SAP_CODE).map(
    ([code, status]): [string, PermitGrantStatus] => [toWireToken(code), status],
  ),
]);

/**
 * Map a wire permit status (code or display label) to its canonical code, or
 * null when unrecognised. The inbound handler stores null as UNRECOGNISED rather
 * than rejecting the message, so a vocabulary change at SAP never drops an event.
 * Also resolves SAP permit task codes (PERMIT_GRANT_STATUS_BY_SAP_CODE).
 */
export function normalizePermitGrantStatus(value: unknown): PermitGrantStatus | null {
  if (typeof value !== 'string') return null;
  return PERMIT_GRANT_STATUS_BY_WIRE_TOKEN.get(toWireToken(value)) ?? null;
}

export const PERMIT_REQUEST_TYPE = {
  INITIAL: 'initial',
  EXTENSION: 'extension',
  START: 'start',
  STOP: 'stop',
  GENERAL: 'general',
  // CWL-411 — carry no permit lifecycle change.
  STATUS: 'status',
  NSG: 'nsg',
} as const;

export const permitRequestTypeSchema = z.enum([
  PERMIT_REQUEST_TYPE.INITIAL,
  PERMIT_REQUEST_TYPE.EXTENSION,
  PERMIT_REQUEST_TYPE.START,
  PERMIT_REQUEST_TYPE.STOP,
  PERMIT_REQUEST_TYPE.GENERAL,
  PERMIT_REQUEST_TYPE.STATUS,
  PERMIT_REQUEST_TYPE.NSG,
]);
export type PermitRequestType = z.infer<typeof permitRequestTypeSchema>;

/** `status` message permit payload. permitNumber is absent for the pre-SAP states. */
export const permitStatusSchema = z.object({
  permitNumber: z.string().min(1).optional(),
  /** The permit reference number (SAP `PermitRefNo`) SAP populates once the permit is granted (CWL-144); sent with `granted`. */
  permitReferenceNumber: z.string().optional(),
  // Free string: an unknown value is stored as 'unrecognised', never a 400.
  permitStatus: z.string().min(1),
  statusReason: z.string().optional(),
  actualStart: z.string().datetime({ offset: true }).nullish(),
});
export type InboundPermitStatus = z.infer<typeof permitStatusSchema>;

export const NSG_STATUS = {
  REQUESTED: 'requested',
  RECEIVED: 'received',
  FAILED: 'failed',
} as const;
export const nsgStatusSchema = z.enum([
  NSG_STATUS.REQUESTED,
  NSG_STATUS.RECEIVED,
  NSG_STATUS.FAILED,
]);
export type NsgStatus = z.infer<typeof nsgStatusSchema>;
export const NSG_STATUS_LABEL: Record<NsgStatus, string> = {
  [NSG_STATUS.REQUESTED]: 'NSG requested',
  [NSG_STATUS.RECEIVED]: 'NSG received',
  [NSG_STATUS.FAILED]: 'Permit request failed',
};

const NPR_FORM_VALUE_MAX_LENGTH = 2000;
/** One Arrange Permit value relayed from the SAP NSG response: text, or a checkbox selection (CWL-328). */
const nprFormValueSchema = z.union([
  z.string().max(NPR_FORM_VALUE_MAX_LENGTH),
  z.array(z.string().max(NPR_FORM_VALUE_MAX_LENGTH)).max(20),
]);

/** NSG request state or response. Bounds match works_lite_permit_nsg columns. */
export const nsgSchema = z.object({
  status: nsgStatusSchema,
  usrn: z.string().max(20).optional(),
  specialDesignationCode: z.string().max(50).optional(),
  localConsiderations: z.string().optional(),
  receivedAt: z.string().datetime({ offset: true }).optional(),
  /** Form-ready NSG values keyed by Arrange Permit mapping key; the bound matches ignite-core sap-nsg.ts. */
  formValues: z.record(z.string().max(60), nprFormValueSchema).optional(),
});
export type InboundPermitNsg = z.infer<typeof nsgSchema>;

/** CORE's existing CWL-147 SAP permit error payload (ignite-core handle-permit-error.ts). */
export const permitErrorSchema = z.object({
  message: z.string().min(1),
  // CORE sends null when SAP's Task is blank and on the CWL-443 revert relay.
  task: z.string().nullish(),
});
export type InboundPermitError = z.infer<typeof permitErrorSchema>;

// ---------------------------------------------------------------------------
// Inbound body — CORE → LITE
// ---------------------------------------------------------------------------

export const inboundPermitBodySchema = z.object({
  meta: z
    .object({
      messageId: z.string().uuid(),
      eventType: z.string(),
      sentAt: z.string(),
      source: z.string().optional(),
      target: z.string().optional(),
      correlationId: z.string().optional(),
      igniteEventId: z.string().optional(),
      schemaVersion: z.string().optional(),
      deliveryPartnerCode: z.string().optional(),
      version: z.string().optional(),
    })
    .passthrough(),
  workOrder: z
    .object({
      id: z.string(),
      // Required on every message except `nsg` and a permitError (enforced below).
      permit: z
        .object({
          permitNumber: z.string().optional(),
          permitReferenceNumber: z.string().optional(),
          // Legacy — earlier CORE builds used this key. Accepted so in-flight
          // messages don't fail schema validation.
          permitReference: z.string().optional(),
          startDate: z.string().optional(),
          endDate: z.string().optional(),
          actualStart: z.string().nullish(),
          extendDate: z.string().optional(),
          // Permit category resolved by CORE after the SAP DISP confirmation.
          // Present on the "Permit Category" event, which carries no
          // permitRequestType and does not touch permit lifecycle rows.
          //
          // Accepted as a free string, not an enum: CORE sends the lowercase codes
          // today, but its integration doc and activity log both use the display
          // labels ('Planned' / 'Immediate' / 'No Permit'). An enum here rejects
          // the whole message with a 400 before the handler runs, so the category
          // would never land and the job would stay gated forever (CWL-125). The
          // handler resolves both spellings via normalizePermitCategory() and
          // throws on anything genuinely unrecognised, so a bad value still fails
          // loudly — just with a message that names the offending category.
          permitCategory: z.string().optional(),
          // CWL-411 `status` message (validated by permitStatusSchema below).
          permitStatus: z.string().optional(),
          statusReason: z.string().optional(),
        })
        .passthrough()
        .optional(),
      // Optional: the Permit Category and permitError events omit it.
      permitRequestType: permitRequestTypeSchema.optional(),
      nsg: nsgSchema.optional(),
      permitError: permitErrorSchema.optional(),
    })
    .superRefine((workOrder, ctx) => {
      if (workOrder.permitRequestType === PERMIT_REQUEST_TYPE.NSG) {
        if (!workOrder.nsg) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nsg'],
            message: "Required when permitRequestType is 'nsg'",
          });
        }
        return;
      }
      // The CWL-147 error payload carries no permit object.
      if (!workOrder.permitRequestType && workOrder.permitError) return;
      if (!workOrder.permit) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['permit'], message: 'Required' });
        return;
      }
      if (workOrder.permitRequestType === PERMIT_REQUEST_TYPE.STATUS) {
        const result = permitStatusSchema.safeParse(workOrder.permit);
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['permit', ...issue.path],
              message: issue.message,
            });
          }
        }
      }
    }),
});

export type InboundPermitBody = z.infer<typeof inboundPermitBodySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const workOrderPermitDirectionalContract = c.router({
  // INBOUND — CORE → LITE
  inboundPermitUpdate: {
    method: 'POST' as const,
    path: '/inbound/permit-update',
    body: inboundPermitBodySchema,
    responses: {
      200: inboundPermitResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: permit update',
    description:
      'Receives work_order.permit_update from CORE. If permit.permitCategory is present (and permitRequestType absent), records the permit category on the job. Otherwise routes on permitRequestType: initial (create permit record), extension (update extendDate), start (update startDate), stop (update endDate), general (apply any provided date fields), status (record permit outcome / NPR state), nsg (record NSG request or response); a permitError with no permitRequestType records the SAP failure reason.',
  },
});
